import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { memberApi } from '@formosa/shared/api/member';
import {
  clearMemberSession,
  getMemberPermissions,
  getMemberToken,
  getMemberUser,
  saveMemberSession,
} from '@formosa/shared/auth/tokenStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getMemberToken());
  const [user, setUser] = useState(() => getMemberUser());
  const [permissions, setPermissions] = useState(() => getMemberPermissions());

  const loginSuccess = useCallback((data) => {
    const { authToken, member, permissions: perms } = data || {};
    saveMemberSession({
      authToken,
      member,
      permissions: perms,
    });
    setToken(authToken);
    setUser(member || null);
    setPermissions(perms || []);
  }, []);

  const logout = useCallback(() => {
    clearMemberSession();
    setToken(null);
    setUser(null);
    setPermissions([]);
  }, []);

  const refreshUser = useCallback(async () => {
    const authToken = getMemberToken();
    if (!authToken) {
      return null;
    }
    const member = await memberApi.getInfo();
    saveMemberSession({ authToken, member });
    setUser(member || null);
    return member;
  }, []);

  useEffect(() => {
    const handleExpired = () => logout();
    window.addEventListener('formosa:auth-expired', handleExpired);
    return () => window.removeEventListener('formosa:auth-expired', handleExpired);
  }, [logout]);

  const value = useMemo(
    () => ({
      token,
      user,
      permissions,
      isAuthenticated: Boolean(token),
      loginSuccess,
      logout,
      refreshUser,
    }),
    [token, user, permissions, loginSuccess, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必須在 AuthProvider 內使用');
  }
  return context;
}
